export const getParentPath = (planIndex: number = 0, coverageIndex: number = 0) =>
  `benefitPackagingDetail.plans[${planIndex}].coverageDefs[${coverageIndex}]`;

export const getParentStructurePath = (planIndex: number = 0, coverageIndex: number = 0) =>
  `${getParentPath(planIndex, coverageIndex)}.benefitStructure`;

export const getParentSchedulePath = (planIndex: number = 0, coverageIndex: number = 0) =>
  `${getParentPath(planIndex, coverageIndex)}.benefitSchedule`;

export const getFundingStructurePropertyPaths = (
  planIndex: number = 0,
  coverageIndex: number = 0,
  productType: string = ''
) => {
  const parentPath = `${getParentPath(planIndex, coverageIndex)}.fundingStructure`;

  const payrollFrequency: {[key: string]: string} = {
    HI: `${parentPath}.payrollFrequency`,
    CI: `${parentPath}.payrollFrequency`,
    ACC: `${parentPath}.payrollFrequencyCd`,
  };

  return {
    _key: `${parentPath}._key`,
    _type: `${parentPath}._type`,
    contributionBasisCd: `${parentPath}.contributionBasisCd`,
    contributionTypeCd: `${parentPath}.contributionTypeCd`,
    fundingTypeCd: `${parentPath}.fundingTypeCd`,
    groupContributionPct: `${parentPath}.groupContributionPct`,
    groupPaidPortionAppliesTo: `${parentPath}.groupPaidPortionAppliesTo`,
    minParticipantsNum: `${parentPath}.minParticipantsNum`,
    payrollFrequencyCd: payrollFrequency[productType],
    requiredGroupAccessPct: `${parentPath}.requiredGroupAccessPct`,
    requiredGroupAccessPctCd: `${parentPath}.requiredGroupAccessPctCd`,
    requiredParticipationPct: `${parentPath}.requiredParticipationPct`,
    sponsorContributionAmount: `${parentPath}.sponsorContributionAmount`,
    paidBenefitAppliesToCd: `${parentPath}.paidBenefitAppliesToCd `,
    taxabilityCd: `${parentPath}.taxabilityCd`,
  };
};

export const getCoverageDefinitionsPaths = (
  planIndex: number = 0,
  coverageIndex: number = 0,
  eligibilityIndex?: number
) => {
  const parentPath = `${getParentPath(planIndex, coverageIndex)}`;

  return {
    _key: `${parentPath}._key`,
    _type: `${parentPath}._type`,
    whoCoveredCd: `${parentPath}.whoCoveredCd`,
    benefitAmountSpouse: `${parentPath}.benefitAmountSpouse.benefitPct`,
    benefitAmountChild: `${parentPath}.benefitAmountChild.benefitPct`,
    terminationAge: `${parentPath}.terminationAge`,
    planStructure: `${parentPath}.planStructure`,
    benefitPayment: `${parentPath}.benefitPayment`,
    rateGuaranteeMonths: `rateGuaranteeRenewalDetails.rateGuaranteeMonths`,
    coverageBasisCd: `${parentPath}.coverageBasisCd`,
    censusTypeCd: `${parentPath}.censusTypeCd`,
    minHourlyReq: `${parentPath}.eligibilities[${eligibilityIndex}].minHourlyReq`,
    minAgeIndividual: `${parentPath}.issueAge.minAgeIndividual`,
    minAgeChildren: `${parentPath}.issueAge.minAgeChildren`,
    maxAgeChildren: `${parentPath}.issueAge.maxAgeChildren`,
    rateBasisCd: `${parentPath}.ratingDetails.rateBasisCd`,
    rateFormatCd: `${parentPath}.ratingDetails.rateFormatCd`,
    underwritingTypeCd: `${parentPath}.underwritingDetails.underwritingTypeCd`,
    preExLimitationAndPregnancyWaitingPeriodCd: `${parentPath}.preExLimitationAndPregnancyWaitingPeriodDetails.preExLimitationAndPregnancyWaitingPeriodCd`,
    preExistingConditionLimitationPeriodCd: `${parentPath}.preExLimitationAndPregnancyWaitingPeriodDetails.preExistingConditionLimitationPeriodCd`,
    pregnancyWaitingPeriodMonthsCd: `${parentPath}.preExLimitationAndPregnancyWaitingPeriodDetails.pregnancyWaitingPeriodMonthsCd`,
    assumedParticipationPct: `${parentPath}.assumedParticipationPct`,
  };
};

export const getGCI5HealthScreeningServicesPaths = (planIndex: number = 0, coverageIndex: number = 0) => {
  const parentPath = `${getParentSchedulePath(planIndex, coverageIndex)}.healthScreeningDetails`;

  return {
    _key: `${parentPath}._key`,
    _type: `${parentPath}._type`,
    healthScreeningType: `${parentPath}.healthScreeningType`,
    healthScreeningHashUnits: `${parentPath}.healthScreeningHashUnits`,
    timePeriodCd: `${parentPath}.timePeriodCd`,
    benefitAmount: `${parentPath}.benefitAmount.amount`,
    isPreventativeEnabled: `${parentPath}.isPreventativeEnabled`,
  };
};

export const getBenefitPaths = (planIndex: number = 0, coverageIndex: number = 0, benefitName?: string) => {
  const parentPath = getParentStructurePath(planIndex, coverageIndex);

  return {
    unitsOfAccAccidentSurgeries: `${parentPath}.unitsOfAccAccidentSurgeries`,
    unitsOfDiagnosticPainManagement: `${parentPath}.unitsOfDiagnosticPainManagement`,
    schedule: `${parentPath}.scheduleOfBenefitEnhancementRider`,
    unitsOfAccidentInjuries: `${parentPath}.unitsOfAccidentInjuries`,
    unitsOfEmergencyRoomServices: `${parentPath}.${benefitName}.unitsOfEmergencyRoomServices`,
    _key: `${parentPath}.${benefitName}._key`,
    _type: `${parentPath}.${benefitName}._type`,
    incurralPeriod: `${parentPath}.${benefitName}.incurralPeriod`,
    benefitAmount: `${parentPath}.${benefitName}.benefitAmount.amount`,
    maxBenefitNumber: `${parentPath}.${benefitName}.maxBenefitNumber`,
    timePeriodCd: `${parentPath}.${benefitName}.timePeriodCd`,
    withoutRepairAmount: `${parentPath}.${benefitName}.withoutRepairAmount.amount`,
    withRepairAmount: `${parentPath}.${benefitName}.withRepairAmount.amount`,
    customizeBenefitAmount: `${parentPath}.${benefitName}.customizeBenefitAmount`,
    cATCustomizeBenefitAmount: `${parentPath}.${benefitName}.cATCustomizeBenefitAmount`,
    cATBenefitAmount: `${parentPath}.${benefitName}.cATBenefitAmount.amount`,
    eEGCustomizeBenefitAmount: `${parentPath}.${benefitName}.eEGCustomizeBenefitAmount`,
    eEGBenefitAmount: `${parentPath}.${benefitName}.eEGBenefitAmount.amount`,
    mRICustomizeBenefitAmount: `${parentPath}.${benefitName}.mRICustomizeBenefitAmount`,
    mRIBenefitAmount: `${parentPath}.${benefitName}.mRIBenefitAmount.amount`,
    pETCustomizeBenefitAmount: `${parentPath}.${benefitName}.pETCustomizeBenefitAmount`,
    pETBenefitAmount: `${parentPath}.${benefitName}.pETBenefitAmount.amount`,
    ultrasoundCustomizeBenefitAmount: `${parentPath}.${benefitName}.ultrasoundCustomizeBenefitAmount`,
    ultrasoundBenefitAmount: `${parentPath}.${benefitName}.ultrasoundBenefitAmount.amount`,
    epiduralInjection: `${parentPath}.${benefitName}.epiduralInjection`,
    nerveAblationProcedure: `${parentPath}.${benefitName}.nerveAblationProcedure`,
  };
};

export const getBenefitArrayPaths = (
  planIndex: number,
  coverageIndex: number,
  benefitName: string,
  benefitIndex?: number
) => {
  const parentPath = `${getParentSchedulePath(planIndex, coverageIndex)}.${benefitName}`;
  const parentArrayPath = `${getParentSchedulePath(planIndex, coverageIndex)}.${benefitName}[${benefitIndex}]`;
  const benefitSchedulePath = `${getParentSchedulePath(planIndex, coverageIndex)}`;
  return {
    heartAttackPct: `${parentArrayPath}.heartAttackPct`,
    strokePct: `${parentArrayPath}.strokePct`,
    kidneyRenalFailurePct: `${parentArrayPath}.kidneyRenalFailurePct`,
    majorOrganFailurePct: `${parentArrayPath}.majorOrganFailurePct`,
    coronaryArteryDiseasePct: `${parentArrayPath}.coronaryArteryDiseasePct`,
    tiaOrRindPct: `${parentArrayPath}.tiaOrRindPct`,
    crohnDiseasePct: `${parentArrayPath}.crohnDiseasePct`,
    boneMarrowOrStemCellDonationPct: `${parentArrayPath}.boneMarrowOrStemCellDonationPct`,
    boneMarrowOrStemCellTransplantPct: `${parentArrayPath}.boneMarrowOrStemCellTransplantPct`,
    isCardiopulmonaryEnhancement: `${benefitSchedulePath}.isCardiopulmonaryEnhancement`,
    suddenCardiacArrestPct: `${parentArrayPath}.suddenCardiacArrestPct`,
    idiopathicPulmonaryFibrosisPct: `${parentArrayPath}.idiopathicPulmonaryFibrosisPct`,
    pulmonaryEmbolismPct: `${parentArrayPath}.pulmonaryEmbolismPct`,
    coronaryArteryDiseaseAngioplastyPct: `${parentArrayPath}.coronaryArteryDiseaseAngioplastyPct`,
    aorticOrMitralValveRepairOrReplacementPct: `${parentArrayPath}.aorticOrMitralValveRepairOrReplacementPct`,
    arrhythmiaPacemakerPlacementPct: `${parentArrayPath}.arrhythmiaPacemakerPlacementPct`,
    arrhythmiaICDPlacementPct: `${parentArrayPath}.arrhythmiaICDPlacementPct`,
    isSupplementalCriticalIllnessApplied: `${benefitSchedulePath}.isSupplementalCriticalIllnessApplied`,
    advancedAlzheimersPct: `${parentArrayPath}.advancedAlzheimersPct`,
    advancedParkinsonsPct: `${parentArrayPath}.advancedParkinsonsPct`,
    benignBrainTumorPct: `${parentArrayPath}.benignBrainTumorPct`,
    comaPct: `${parentArrayPath}.comaPct`,
    lossOfHearingPct: `${parentArrayPath}.lossOfHearingPct`,
    lossOfSightPct: `${parentArrayPath}.lossOfSightPct`,
    lossOfSpeechPct: `${parentArrayPath}.lossOfSpeechPct`,
    paralysisPct: `${parentArrayPath}.paralysisPct`,
    occupationalHIVPct: `${parentArrayPath}.occupationalHIVPct`,
    invasiveCancerPct: `${parentArrayPath}.invasiveCancerPct`,
    carcinomaSituPct: `${parentArrayPath}.carcinomaSituPct`,
    childhoodConditionsPct: `${parentPath}.childhoodConditionsPct`,
    neonatalICUPct: `${parentPath}.neonatalICUPct`,
    waiverTypeCd: `${parentPath}.waiverTypeCd`,
    waitingPeriod: `${parentPath}.waitingPeriod`,
    benefitDuration: `${parentPath}.benefitDuration`,
    benefitTypeCd: `${parentPath}.benefitTypeCd`,
    benefitAmount: `${parentPath}.benefitAmount.amount`,
    timePeriod: `${parentPath}.timePeriod`,
    numOfUnits: `${parentPath}.numOfUnits`,
    maxNumberConsultationOrEvaluations: `${parentPath}.maxNumberConsultationOrEvaluations`,
    nonLocalTravelDistance: `${parentPath}.nonLocalTravelDistance`,
    airTransportationBenefitAmount: `${parentPath}.airTransportationBenefitAmount.amount`,
    groundTransportationBenefitAmount: `${parentPath}.groundTransportationBenefitAmount.amount`,
    groundTransportationMaxMiles: `${parentPath}.groundTransportationMaxMiles`,
    maxBenefitAmount: `${parentPath}.maxBenefitAmount.amount`,
    planBnfAmountPct: `${parentPath}.planBnfAmountPct`,
    planBenefitAmountPct: `${parentPath}.planBenefitAmountPct`,
    additionalEligiblePrograms: `${parentPath}.additionalEligiblePrograms`,
    maxDaysPerPeriod: `${parentPath}.maxDaysPerPeriod`,
    injuryPct: `${parentPath}.injuryPct`,
  };
};

export const getCoverageDefinitionPropertyPaths = (
  planIndex: number = 0,
  coverageIndex: number = 0,
  productType: string = ''
) => {
  const parentPath = `${getParentPath(planIndex, coverageIndex)}`;

  const terminationAgeProperties: {[key: string]: string} = {
    HI: `${parentPath}.terminationAgeCd`,
    CI: `${parentPath}.terminationAgeCd`,
    ACC: `${parentPath}.terminationAge`,
  };

  const planStructureProperties: {[key: string]: string} = {
    HI: `${parentPath}.planStructure`,
    CI: `${parentPath}.planStructure`,
    ACC: `${parentPath}.coverageBasisCd`,
  };
  return {
    whoCoveredCd: `${parentPath}.whoCoveredCd`,
    benefitAmountSpouseBenefitPct: `${parentPath}.benefitAmountSpouse.benefitPct`,
    benefitAmountChildBenefitPct: `${parentPath}.benefitAmountChild.benefitPct`,
    terminationAge: terminationAgeProperties[productType],
    planStructure: planStructureProperties[productType],
    benefitPaymentType: `${parentPath}.benefitPaymentType`,
    rateGuaranteeMonths: `rateGuaranteeRenewalDetails.rateGuaranteeMonths`,
    benefitPayment: `${parentPath}.benefitPayment`,
  };
};

export const getRatingPropertyPaths = (planIndex: number = 0, coverageIndex: number = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageIndex)}.ratingDetails`;

  return {
    rateBasisCd: `${parentPath}.rateBasisCd`,
    rateFormatCd: `${parentPath}.rateFormatCd`,
  };
};

export const getEligibilityPropertyPaths = (
  planIndex: number = 0,
  coverageIndex: number = 0,
  productType: string = ''
) => {
  const parentPath = `${getParentPath(planIndex, coverageIndex)}`;

  const minIssueAge: {[key: string]: string} = {
    HI: `${parentPath}.issueAges.minIssueAgeIndividual`,
    ACC: `${parentPath}.issueAge.minAgeIndividual`,
  };

  const maxAgeChildren: {[key: string]: string} = {
    HI: `${parentPath}.issueAges.maxIssueAgeChild`,
    ACC: `${parentPath}.issueAge.maxAgeChildren`,
  };

  const minAgeChildren: {[key: string]: string} = {
    HI: `${parentPath}.issueAges.minIssueAgeChild`,
    ACC: `${parentPath}.issueAge.minAgeChildren`,
  };

  return {
    censusTypeCd: `${getParentPath(planIndex, coverageIndex)}.censusTypeCd`,
    assumedParticipationPct: `${getParentPath(planIndex, coverageIndex)}.assumedParticipationPct`,
    waitingPeriodAmount: `${parentPath}.eligibilities.waitingPeriodAmount`,
    waitingPeriodModeCd: `${parentPath}.eligibilities.waitingPeriodModeCd`,
    minHourlyReq: `${parentPath}.eligibilities.minHourlyReq`,
    eligibilityTypeCd: `${parentPath}.eligibilities.eligibilityTypeCd`,
    waitingPeriodDefCd: `${parentPath}.eligibilities.waitingPeriodDefCd`,
    newParticipant: {
      minHourlyReq: `${parentPath}.eligibilities[1].minHourlyReq`,
    },
    issuesAges: {
      minIssueAgeIndividual: minIssueAge[productType],
      maxIssueAgeChild: maxAgeChildren[productType],
      minIssueAgeChild: minAgeChildren[productType],
    },
    issueAge: {
      minIssueAgeIndividual: `${parentPath}.issueAge.minAgeIndividual`,
      maxIssueAgeChild: `${parentPath}.issueAge.maxAgeChildren`,
      minIssueAgeChild: `${parentPath}.issueAge.minAgeChildren`,
    },
  };
};

export const getExclusionsAndLimitationsPropertyPaths = (planIndex: number = 0, coverageIndex: number = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageIndex)}`;
  return {
    preExLimitationAndPregnancyWaitingPeriodDetails: {
      pregnancyWaitingPeriodMonthsCd: `${parentPath}.preExLimitationAndPregnancyWaitingPeriodDetails.pregnancyWaitingPeriodMonthsCd`,
      preExistingConditionLimitationPeriodCd: `${parentPath}.preExLimitationAndPregnancyWaitingPeriodDetails.preExistingConditionLimitationPeriodCd`,
      preExLimitationAndPregnancyWaitingPeriodCd: `${parentPath}.preExLimitationAndPregnancyWaitingPeriodDetails.preExLimitationAndPregnancyWaitingPeriodCd`,
    },
  };
};

export const getBenefitSchedulePropertyPaths = (
  benefitStructureIndex: number,
  planIndex: number = 0,
  coverageIndex: number = 0
) => {
  const parentPath = `${getParentPath(planIndex, coverageIndex)}.benefitStructures[${benefitStructureIndex}]`;
  const benefitSchedulePath = `${getParentSchedulePath(planIndex, coverageIndex)}`;

  return {
    benefitTypeCd: `${parentPath}.benefitTypeCd`,
    benefitAmount: `${parentPath}.benefitAmount.amount`,
    isLifetimeMaximumApplied: `${benefitSchedulePath}.benefitAmount`,
    maxBenefitAmountPct: `${benefitSchedulePath}.lifetimeMaximumDetails.benefitAmount`,
    isRecurrenceBenefitApplied: `${benefitSchedulePath}.isRecurrenceBenefitApplied`,
  };
};

export const getBenefitScheduleBenefitStructurePropertyPaths = (
  benefitStructureIndices: number[],
  planIndex: number = 0,
  coverageIndex = 0
) => {
  const benefitStructurePaths = benefitStructureIndices.map(
    benefitStructureIndex => `${getParentPath(planIndex, coverageIndex)}.benefitStructures[${benefitStructureIndex}]`
  );

  return benefitStructurePaths.map(path => ({
    benefitTypeCd: `${path}.benefitTypeCd`,
    benefitAmount: `${path}.benefitAmount.amount`,
    maxBenefitAmount: `${path}.maxBenefitAmount.amount`,
    minBenefitAmount: `${path}.minBenefitAmount.amount`,
    individualAmtPct: `${path}.individualAmtPct`,
  }));
};

export const getPortabilityPaths = (planIndex: number = 0, coverageIndex: number = 0) => {
  const parentPath = `${getParentPath(planIndex, coverageIndex)}`;

  return {
    isPortabilityApplied: `${parentPath}.benefitStructure.isPortabilityApplied`,
    limitPortedCovDurationInd: `${parentPath}.benefitStructure.portabilityDetails.limitPortedCovDurationInd`,
    covDuration: `${parentPath}.benefitStructure.portabilityDetails.covDuration`,
  };
};

export const getUnderwritingPropertyPaths = (planIndex: number = 0, coverageIndex: number = 0) => {
  const parentPath = `benefitPackagingDetail.plans[${planIndex}].coverageDefs[${coverageIndex}].underwritingDetails`;
  return {
    underwritingTypeCd: `${parentPath}.underwritingTypeCd`,
    indGIAmount: `${parentPath}.indGIAmount.amount`,
    isEOIRequired: `${parentPath}.isEOIRequired`,
  };
};
